import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { flowisMaxLimit, type FlowisDocumentSummary, type FlowisPagedResponse } from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { GateStatusChip } from '../../components/StatusChips';
import { formatAmount, formatDate } from '../../utils/format';

export interface FlowisDocumentsTableProps {
  page: FlowisPagedResponse<FlowisDocumentSummary> | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  onSelect: (documentId: string) => void;
  offset: number;
  limit: number;
  onOffsetChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  /** Extra trailing column, e.g. the "Download to AlgoBridge" action on the received tab. */
  actionHeader?: string;
  renderAction?: (row: FlowisDocumentSummary) => ReactNode;
}

export function FlowisDocumentsTable({
  page,
  isPending,
  error,
  onRetry,
  onSelect,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  actionHeader,
  renderAction,
}: FlowisDocumentsTableProps) {
  const { t } = useTranslation();

  if (isPending) return <LoadingBlock label={t('flowis.asking')} />;
  if (error) return <ErrorBlock error={error} onRetry={onRetry} />;
  if (!page || page.results.length === 0) return <EmptyBlock message={t('flowis.empty')} />;

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('flowis.columns.invoiceNo')}</TableCell>
              <TableCell>{t('flowis.columns.type')}</TableCell>
              <TableCell>{t('flowis.columns.issued')}</TableCell>
              <TableCell>{t('flowis.columns.seller')}</TableCell>
              <TableCell>{t('flowis.columns.buyer')}</TableCell>
              <TableCell align="right">{t('flowis.columns.totalInclVat')}</TableCell>
              <TableCell>{t('columns.status')}</TableCell>
              <TableCell>{t('flowis.columns.consumed')}</TableCell>
              {renderAction && <TableCell align="right">{actionHeader}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {page.results.map((row, index) => (
              <TableRow
                key={row.id ?? `row-${index}`}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  // A row the gate answered without an id has no detail to open.
                  if (row.id !== null) onSelect(row.id);
                }}
                role="button"
              >
                <TableCell>{row.invoiceId ?? '—'}</TableCell>
                <TableCell>{row.documentType ?? '—'}</TableCell>
                <TableCell>{formatDate(row.issueDate)}</TableCell>
                <TableCell>{row.sellerRegistrationName}</TableCell>
                <TableCell>{row.buyerRegistrationName}</TableCell>
                <TableCell align="right">
                  {formatAmount(row.totalVatIncluded, row.documentCurrencyCode)}
                </TableCell>
                <TableCell>
                  <GateStatusChip status={row.status} />
                </TableCell>
                <TableCell>
                  {row.downloaded ? <Chip size="small" label={t('states.yes')} variant="outlined" /> : '—'}
                </TableCell>
                {renderAction && (
                  <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                    {renderAction(row)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={page.count ?? page.results.length}
        page={Math.floor(offset / Math.max(1, limit))}
        rowsPerPage={limit}
        rowsPerPageOptions={[10, 25, 50, flowisMaxLimit]}
        onPageChange={(_event, nextPage) => onOffsetChange(nextPage * limit)}
        onRowsPerPageChange={(event) => onLimitChange(Number(event.target.value))}
        // The gate's next/previous are absolute URLs on its own host and are never followed; they
        // are read only as "is there more", and the offsets are computed here.
        nextIconButtonProps={{ disabled: page.next === null }}
        backIconButtonProps={{ disabled: page.previous === null }}
      />
    </Paper>
  );
}
